import { CardShell } from '@atlas/ui-components'
import { cn } from '@atlas/ui-primitives'
import { Search, X } from 'lucide-react'
import { Eyebrow } from '@/components/editorial/PageHeader'
import type { GeoOption } from './brand-catalog'

interface PillProps {
	label: string
	/** Brands reachable under the option. Omitted on the "all" pill, which has no rollup. */
	count?: number
	active: boolean
	disabled?: boolean
	onClick: () => void
}

/**
 * One geography choice. An option with no brands still renders, so the coverage gap is
 * visible, but goes inert rather than offering a click into an empty result set.
 */
function Pill({ label, count, active, disabled, onClick }: PillProps) {
	return (
		<button
			type="button"
			disabled={disabled}
			aria-pressed={active}
			onClick={onClick}
			// Without this the count reads as a bare number appended to the label.
			aria-label={count === undefined ? undefined : `${label}, ${count} ${count === 1 ? 'brand' : 'brands'}`}
			title={disabled ? `${label} has no classified assets yet` : undefined}
			className={cn(
				'inline-flex items-center gap-1.5 rounded-ds-full px-3 py-1 text-sm transition-colors',
				active ? 'bg-ds-primary text-ds-primary-foreground' : 'border border-ds-border bg-ds-surface text-ds-text',
				disabled && 'cursor-not-allowed opacity-45',
				!active && !disabled && 'hover:border-ds-text',
			)}
		>
			{label}
			{count === undefined ? null : (
				<span className={cn('font-mono text-xs', active ? 'opacity-70' : 'text-ds-text-subtle')}>{count}</span>
			)}
		</button>
	)
}

interface PillRowProps {
	label: string
	allLabel: string
	options: GeoOption[]
	selected: string | null
	onSelect: (code: string | null) => void
}

function PillRow({ label, allLabel, options, selected, onSelect }: PillRowProps) {
	return (
		<div className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-[5rem_1fr]">
			<Eyebrow className="pt-1.5">{label}</Eyebrow>
			{/* Grouped and named, so the row's pills are not read as loose page-level buttons. */}
			<div role="group" aria-label={label} className="flex flex-wrap gap-1.5">
				<Pill label={allLabel} active={selected === null} onClick={() => onSelect(null)} />
				{options.map((o) => (
					<Pill
						key={o.code}
						label={o.label}
						count={o.brandCount}
						active={selected === o.code}
						disabled={o.brandCount === 0}
						onClick={() => onSelect(o.code)}
					/>
				))}
			</div>
		</div>
	)
}

export interface GeoFilterBarProps {
	regions: GeoOption[]
	/** Countries of the active region. Empty ⇒ the market row is not offered. */
	countries: GeoOption[]
	regionCode: string | null
	countryCode: string | null
	query: string
	onRegionChange: (code: string | null) => void
	onCountryChange: (code: string | null) => void
	onQueryChange: (query: string) => void
	onClearAll: () => void
}

/**
 * Explore's filter surface: free-text brand search over two counted geography rows.
 *
 * Pills rather than dropdowns because the counts are the point: the whole reachable
 * market map, and how much sits under each part of it, is readable without opening
 * anything. Selection state lives on the pills themselves, so there is no second row of
 * chips restating it. Presentational + storied.
 */
export function GeoFilterBar({
	regions,
	countries,
	regionCode,
	countryCode,
	query,
	onRegionChange,
	onCountryChange,
	onQueryChange,
	onClearAll,
}: GeoFilterBarProps) {
	const hasFilters = !!regionCode || !!countryCode || query.trim() !== ''

	return (
		<CardShell className="mb-8 space-y-4">
			<div className="flex flex-wrap items-center justify-between gap-3">
				<div className="relative w-full max-w-80">
					<Search aria-hidden className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ds-text-subtle" />
					<input
						value={query}
						onChange={(e) => onQueryChange(e.target.value)}
						placeholder="Search brands…"
						aria-label="Search brands"
						className="w-full rounded-ds-full border border-ds-border bg-ds-surface py-1.5 pl-9 pr-9 text-sm text-ds-text placeholder:text-ds-text-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring"
					/>
					{query === '' ? null : (
						<button
							type="button"
							onClick={() => onQueryChange('')}
							aria-label="Clear search"
							className="absolute right-3 top-1/2 -translate-y-1/2 text-ds-text-subtle hover:text-ds-text"
						>
							<X className="h-3.5 w-3.5" />
						</button>
					)}
				</div>
				{hasFilters ? (
					<button
						type="button"
						onClick={onClearAll}
						className="text-sm text-ds-text-muted underline-offset-2 hover:text-ds-text hover:underline"
					>
						Clear all
					</button>
				) : null}
			</div>

			<PillRow
				label="Region"
				allLabel="All regions"
				options={regions}
				selected={regionCode}
				onSelect={onRegionChange}
			/>
			{countries.length > 0 ? (
				<PillRow
					label="Market"
					allLabel="All markets"
					options={countries}
					selected={countryCode}
					onSelect={onCountryChange}
				/>
			) : null}
		</CardShell>
	)
}
